import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as executionsAPI from '../../api/executions';

// Async thunks
export const fetchExecutionsForTrade = createAsyncThunk(
  'executions/fetchForTrade',
  async ({ tradeId, options = {} }) => {
    const response = await executionsAPI.getExecutionsForTrade(tradeId, options);
    return { tradeId, ...response };
  }
);

export const addExecutionToTrade = createAsyncThunk(
  'executions/add',
  async ({ tradeId, executionData }) => {
    const response = await executionsAPI.addExecution(tradeId, executionData);
    return { tradeId, execution: response.execution };
  }
);

export const fetchTradeState = createAsyncThunk(
  'executions/fetchTradeState',
  async ({ tradeId, mark = null }) => {
    const response = await executionsAPI.getTradeState(tradeId, mark);
    return { tradeId, tradeState: response.tradeState };
  }
);

export const addAdjustmentToTrade = createAsyncThunk(
  'executions/addAdjustment',
  async ({ tradeId, adjustmentData }) => {
    const response = await executionsAPI.addAdjustment(tradeId, adjustmentData);
    return { tradeId, adjustment: response.adjustment };
  }
);

export const fetchAdjustmentsForTrade = createAsyncThunk(
  'executions/fetchAdjustments',
  async ({ tradeId }) => {
    const response = await executionsAPI.getAdjustmentsForTrade(tradeId);
    return { tradeId, adjustments: response.adjustments };
  }
);

const initialState = {
  // Trade-specific executions data
  executionsByTrade: {},
  adjustmentsByTrade: {},
  tradeStates: {},
  
  // Loading states
  loading: false,
  loadingExecutions: false,
  loadingAdjustments: false,
  loadingTradeState: false,
  
  // Error states
  error: null,
  executionsError: null,
  adjustmentsError: null,
  tradeStateError: null,
  
  // UI state
  showAddExecutionDialog: false,
  showAddAdjustmentDialog: false,
  selectedTradeId: null
};

const executionsSlice = createSlice({
  name: 'executions',
  initialState,
  reducers: {
    clearExecutionsForTrade: (state, action) => {
      const tradeId = action.payload;
      delete state.executionsByTrade[tradeId];
      delete state.adjustmentsByTrade[tradeId];
      delete state.tradeStates[tradeId];
    },
    
    setShowAddExecutionDialog: (state, action) => {
      state.showAddExecutionDialog = action.payload.show;
      state.selectedTradeId = action.payload.tradeId || null;
    },
    
    setShowAddAdjustmentDialog: (state, action) => {
      state.showAddAdjustmentDialog = action.payload.show;
      state.selectedTradeId = action.payload.tradeId || null;
    },
    
    clearErrors: (state) => {
      state.error = null;
      state.executionsError = null;
      state.adjustmentsError = null;
      state.tradeStateError = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch executions
    builder
      .addCase(fetchExecutionsForTrade.pending, (state) => {
        state.loadingExecutions = true;
        state.executionsError = null;
      })
      .addCase(fetchExecutionsForTrade.fulfilled, (state, action) => {
        state.loadingExecutions = false;
        const { tradeId, executions, nextToken } = action.payload;
        state.executionsByTrade[tradeId] = {
          executions,
          nextToken,
          lastFetched: new Date().toISOString()
        };
      })
      .addCase(fetchExecutionsForTrade.rejected, (state, action) => {
        state.loadingExecutions = false;
        state.executionsError = action.error.message;
      });
    
    // Add execution
    builder
      .addCase(addExecutionToTrade.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addExecutionToTrade.fulfilled, (state, action) => {
        state.loading = false;
        const { tradeId, execution } = action.payload;
        
        // Add to existing executions or create new array
        if (!state.executionsByTrade[tradeId]) {
          state.executionsByTrade[tradeId] = { executions: [], nextToken: null };
        }
        state.executionsByTrade[tradeId].executions.push(execution);
        
        // Invalidate trade state cache
        delete state.tradeStates[tradeId];
      })
      .addCase(addExecutionToTrade.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
    
    // Fetch trade state
    builder
      .addCase(fetchTradeState.pending, (state) => {
        state.loadingTradeState = true;
        state.tradeStateError = null;
      })
      .addCase(fetchTradeState.fulfilled, (state, action) => {
        state.loadingTradeState = false;
        const { tradeId, tradeState } = action.payload;
        state.tradeStates[tradeId] = {
          ...tradeState,
          lastFetched: new Date().toISOString()
        };
      })
      .addCase(fetchTradeState.rejected, (state, action) => {
        state.loadingTradeState = false;
        state.tradeStateError = action.error.message;
      });
    
    // Add adjustment
    builder
      .addCase(addAdjustmentToTrade.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addAdjustmentToTrade.fulfilled, (state, action) => {
        state.loading = false;
        const { tradeId, adjustment } = action.payload;
        
        // Add to existing adjustments or create new array
        if (!state.adjustmentsByTrade[tradeId]) {
          state.adjustmentsByTrade[tradeId] = [];
        }
        state.adjustmentsByTrade[tradeId].push(adjustment);
        
        // Invalidate trade state cache
        delete state.tradeStates[tradeId];
      })
      .addCase(addAdjustmentToTrade.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
    
    // Fetch adjustments
    builder
      .addCase(fetchAdjustmentsForTrade.pending, (state) => {
        state.loadingAdjustments = true;
        state.adjustmentsError = null;
      })
      .addCase(fetchAdjustmentsForTrade.fulfilled, (state, action) => {
        state.loadingAdjustments = false;
        const { tradeId, adjustments } = action.payload;
        state.adjustmentsByTrade[tradeId] = adjustments;
      })
      .addCase(fetchAdjustmentsForTrade.rejected, (state, action) => {
        state.loadingAdjustments = false;
        state.adjustmentsError = action.error.message;
      });
  }
});

export const {
  clearExecutionsForTrade,
  setShowAddExecutionDialog,
  setShowAddAdjustmentDialog,
  clearErrors
} = executionsSlice.actions;

// Selectors
export const selectExecutionsForTrade = (state, tradeId) => 
  state.executions.executionsByTrade[tradeId]?.executions || [];

export const selectAdjustmentsForTrade = (state, tradeId) => 
  state.executions.adjustmentsByTrade[tradeId] || [];

export const selectTradeState = (state, tradeId) => 
  state.executions.tradeStates[tradeId];

export const selectExecutionsLoading = (state) => state.executions.loadingExecutions;
export const selectAdjustmentsLoading = (state) => state.executions.loadingAdjustments;
export const selectTradeStateLoading = (state) => state.executions.loadingTradeState;
export const selectExecutionsError = (state) => state.executions.executionsError;
export const selectAdjustmentsError = (state) => state.executions.adjustmentsError;
export const selectTradeStateError = (state) => state.executions.tradeStateError;

export default executionsSlice.reducer;
